import { fromEntries } from 'helpers/general';
import { createSelector, Selector } from 'reselect';
import { MakerSelectors, RootState, Selectors } from 'types';

export function factoryExtraSelectors<S, MSelectors extends MakerSelectors<S> | undefined>(
  baseSelector: Selector<RootState, S>,
  makerSelectors?: MSelectors
): MSelectors extends MakerSelectors<S> ? Selectors<S, MSelectors> : undefined {
  if (!makerSelectors)
    return undefined as MSelectors extends MakerSelectors<S> ? Selectors<S, MSelectors> : undefined;

  const entries = Object.entries(makerSelectors);

  const mappedEntries = entries?.map(([key, makerSelector]) => {
    const getSelector = (): Selector<RootState, unknown> => {
      if (makerSelector instanceof Function) {
        return createSelector(baseSelector, makerSelector) as Selector<
          RootState,
          ReturnType<typeof makerSelector>
        >;
      } else {
        switch (makerSelector.length) {
          case 2:
            return createSelector(baseSelector, makerSelector[0], makerSelector[1]);
          case 3:
            return createSelector(
              baseSelector,
              makerSelector[0],
              makerSelector[1],
              makerSelector[2]
            );
          case 4:
            return createSelector(
              baseSelector,
              makerSelector[0],
              makerSelector[1],
              makerSelector[2],
              makerSelector[3]
            );
          case 5:
            return createSelector(
              baseSelector,
              makerSelector[0],
              makerSelector[1],
              makerSelector[2],
              makerSelector[3],
              makerSelector[4]
            );
          default:
            return createSelector(
              baseSelector,
              makerSelector[0],
              makerSelector[1],
              makerSelector[2],
              makerSelector[3],
              makerSelector[4],
              makerSelector[5]
            );
        }
      }
    };

    return [key, getSelector()];
  });

  return fromEntries(mappedEntries);
}
